import React, { useState, useEffect } from 'react';
import { Settings, Building2, Receipt, Printer, ShoppingCart, Calculator, ShieldCheck, FileText, Download, Key, Copy, Wifi, WifiOff, RefreshCw, CheckCircle2, AlertCircle, MonitorSpeaker, ChefHat, Beer, Package, Truck } from 'lucide-react';
import { settingsApi } from '../utils/api';
import { toast } from 'sonner';
import { AddPrinterWizard } from '../components/AddPrinterWizard';
import LegalProfileSettings from '../components/LegalProfileSettings';
import PrintProfileSettings from '../components/PrintProfileSettings';
import DeliverySettings from '../components/DeliverySettings';

// Status Badge Component for Printers
const StatusBadge = ({ status, label }) => {
  const statusConfig = {
    active: { bg: 'rgba(34, 197, 94, 0.1)', color: 'rgb(34, 197, 94)', icon: '✓' },
    offline: { bg: 'rgba(239, 68, 68, 0.1)', color: 'rgb(239, 68, 68)', icon: '✗' },
    local: { bg: 'rgba(59, 130, 246, 0.1)', color: 'rgb(59, 130, 246)', icon: '💻' },
    needs_agent: { bg: 'rgba(251, 191, 36, 0.1)', color: 'rgb(251, 191, 36)', icon: '⚠️' },
    disabled: { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', icon: '⊝' }
  };

  const config = statusConfig[status] || statusConfig.offline;

  return (
    <div
      style={{
        padding: 'var(--space-2) var(--space-3)',
        background: config.bg,
        color: config.color,
        borderRadius: 'var(--radius-md)',
        fontSize: '0.75rem',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)'
      }}
    >
      <span>{config.icon}</span>
      <span>{label || status}</span>
    </div>
  );
};

export default function Einstellungen() {
  const [activeTab, setActiveTab] = useState('legal');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [companyForm, setCompanyForm] = useState({});
  const [brandingForm, setBrandingForm] = useState({});
  const [posForm, setPosForm] = useState({});
  const [taxForm, setTaxForm] = useState({});
  const [invoiceForm, setInvoiceForm] = useState({});
  const [printers, setPrinters] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showAddPrinterWizard, setShowAddPrinterWizard] = useState(false);
  
  // Agent state
  const [agentHealth, setAgentHealth] = useState(null);
  const [agentPrinters, setAgentPrinters] = useState([]);
  const [checkingAgent, setCheckingAgent] = useState(false);
  const [queuedJobs, setQueuedJobs] = useState(0);
  
  // Configured printers with their print jobs
  const [configuredPrinters, setConfiguredPrinters] = useState([]);
  const [showAddPrinterModal, setShowAddPrinterModal] = useState(false);
  const [selectedPrinterToAdd, setSelectedPrinterToAdd] = useState(null);
  const [selectedPaperSize, setSelectedPaperSize] = useState('80mm');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Bon-Einstellungen
  const [receiptSettings, setReceiptSettings] = useState({
    footer_message: 'Vielen Dank für Ihre Bestellung!',
    footer_subtext: '',
    logo_url: ''
  });
  
  // Print Agent Token state
  const [agentToken, setAgentToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showToken, setShowToken] = useState(true); // Token standardmäßig sichtbar

  useEffect(() => {
    loadSettings();
    checkAgentHealth();
    loadAgentToken();
    loadReceiptSettings();
    
    // Polling für Agent Status (alle 10 Sekunden im Drucker-Tab)
    let agentPollInterval;
    if (activeTab === 'printers') {
      agentPollInterval = setInterval(() => {
        checkAgentHealth();
      }, 10000); // 10 Sekunden
    }
    
    return () => {
      if (agentPollInterval) clearInterval(agentPollInterval);
    };
  }, [activeTab]);

  // Lade Token neu wenn der Tab gewechselt wird
  useEffect(() => {
    if (activeTab === 'printers') {
      loadAgentToken();
    }
  }, [activeTab]);
  
  const checkAgentHealth = async () => {
    setCheckingAgent(true);
    try {
      // Use the new Cloud Print API instead of old settings API
      await loadAgentToken();
    } catch (error) {
      console.error('Error checking agent health:', error);
      setAgentHealth({ agent_running: false, error: 'NETWORK_ERROR' });
    } finally {
      setCheckingAgent(false);
    }
  };
  
  const loadAgentPrinters = async () => {
    // This function is now handled by loadAgentToken
    // Keep for backwards compatibility
  };

  const loadAgentToken = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      // Use new Cloud Print API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      console.log('Cloud Print Status:', data); // Debug log
      
      // IMMER den Status setzen, auch wenn nicht konfiguriert
      const isConnected = data.agent_connected === true;
      setAgentHealth({ agent_running: isConnected });
      
      // Wartende Druckaufträge setzen
      setQueuedJobs(data.queued_jobs || 0);
      
      if (data.configured) {
        // If we have a token in the response, use it, otherwise try to get it from setup
        if (data.agent_token) {
          setAgentToken({
            has_token: true,
            agent_token: data.agent_token,
            agent_status: isConnected ? 'online' : 'offline'
          });
        } else {
          // Get token from setup endpoint
          const setupResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/setup`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const setupData = await setupResponse.json();
          if (setupData.success && setupData.agent_token) {
            setAgentToken({
              has_token: true,
              agent_token: setupData.agent_token,
              agent_status: isConnected ? 'online' : 'offline'
            });
          }
        }
        
        // Use available_printers (from connected agent) if present, otherwise fall back to printers
        const printersToUse = data.available_printers?.length > 0 ? data.available_printers : (data.printers || []);
        setAgentPrinters(printersToUse);
        
        // Load configured printers from printer_config
        if (data.printer_config) {
          setConfiguredPrinters(data.printer_config);
        }
      } else {
        // Nicht konfiguriert
        setAgentToken(null);
        setAgentPrinters([]);
      }
    } catch (error) {
      console.error('Error loading cloud print status:', error);
      setAgentHealth({ agent_running: false, error: 'NETWORK_ERROR' });
    }
  };

  // Save configured printers to backend
  const saveConfiguredPrinters = async (printersList) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/printers`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ printers: printersList })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Drucker-Konfiguration gespeichert');
      } else {
        toast.error(data.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving printer config:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Add a new printer configuration
  const addPrinter = (printerName, paperSize = '80mm') => {
    const existing = configuredPrinters.find(p => p.name === printerName);
    if (existing) {
      toast.error('Drucker bereits konfiguriert');
      return;
    }
    
    const newPrinter = {
      name: printerName,
      displayName: printerName,
      enabled: true,
      paperSize: paperSize, // '58mm', '80mm', 'a4'
      printJobs: {
        customer: false,
        kitchen: false,
        bar: false,
        delivery: false,
        packing_slip: false,
        daily_report: false
      }
    };
    
    const updated = [...configuredPrinters, newPrinter];
    setConfiguredPrinters(updated);
    // Nicht mehr automatisch speichern - User muss auf Speichern klicken
    setShowAddPrinterModal(false);
    toast.success(`${printerName} hinzugefügt - bitte speichern nicht vergessen!`);
  };

  // Testdruck für einzelnen Drucker
  const sendTestPrint = async (printer) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/print`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: `test_${Date.now()}`,
          job_type: 'customer',
          content_type: 'receipt_data',
          printer_name: printer.name,
          data: {
            order_type: 'pickup',
            order_number: 'TEST',
            items: [
              { name: 'Testdruck', quantity: 1, total_gross: 0.00 }
            ],
            total_gross: 0.00,
            payment_method: 'cash',
            paper_size: printer.paperSize || '80mm'
          }
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Testdruck an ${printer.displayName || printer.name} gesendet!`);
      } else {
        toast.error(data.detail || 'Testdruck fehlgeschlagen');
      }
    } catch (error) {
      console.error('Test print error:', error);
      toast.error('Testdruck fehlgeschlagen');
    }
  };

  // Update printer configuration (without auto-save)
  const updatePrinterConfig = (printerName, field, value) => {
    const updated = configuredPrinters.map(p => {
      if (p.name === printerName) {
        if (field.startsWith('printJobs.')) {
          const jobType = field.split('.')[1];
          return { ...p, printJobs: { ...p.printJobs, [jobType]: value } };
        }
        return { ...p, [field]: value };
      }
      return p;
    });
    setConfiguredPrinters(updated);
    setHasUnsavedChanges(true);
    // Nicht mehr automatisch speichern
  };

  // Drucker-Konfiguration manuell speichern
  const handleSavePrinterConfig = async () => {
    await saveConfiguredPrinters(configuredPrinters);
    setHasUnsavedChanges(false);
  };

  // Remove a printer configuration
  const removePrinter = (printerName) => {
    const updated = configuredPrinters.filter(p => p.name !== printerName);
    setConfiguredPrinters(updated);
    setHasUnsavedChanges(true);
    toast.success('Drucker entfernt - bitte speichern');
  };

  // Bon-Einstellungen speichern
  const saveReceiptSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          receipt_settings: newSettings
        })
      });
      const data = await response.json();
      if (data.success !== false) {
        toast.success('Bon-Einstellungen gespeichert');
      }
    } catch (error) {
      console.error('Error saving receipt settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Bon-Einstellungen laden
  const loadReceiptSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.receipt_settings) {
        setReceiptSettings({
          footer_message: data.receipt_settings.footer_message || 'Vielen Dank für Ihre Bestellung!',
          footer_subtext: data.receipt_settings.footer_subtext || '',
          logo_url: data.branding?.logo_url || ''
        });
      }
    } catch (error) {
      console.error('Error loading receipt settings:', error);
    }
  };

  const generateToken = async () => {
    try {
      setTokenLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Use new Cloud Print API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.agent_token) {
        setAgentToken({
          has_token: true,
          agent_token: data.agent_token,
          agent_status: 'pending'
        });
        setShowToken(true);
        toast.success(data.is_new ? 'Neuer Token generiert!' : 'Token geladen');
      } else {
        toast.error(data.detail || 'Fehler beim Generieren des Tokens');
      }
    } catch (error) {
      console.error('Error generating token:', error);
      toast.error('Netzwerkfehler');
    } finally {
      setTokenLoading(false);
    }
  };

  const copyToken = () => {
    if (agentToken?.agent_token) {
      navigator.clipboard.writeText(agentToken.agent_token);
      toast.success('Token in Zwischenablage kopiert!');
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsApi.get();
      setSettings(data);
      
      // Initialize forms
      setCompanyForm(data.company || {});
      setBrandingForm(data.branding || {});
      setPosForm(data.pos || {});
      setTaxForm(data.tax || {});
      setInvoiceForm(data.invoice || {});
      setPrinters(data.printers?.items || []);
      setLogoPreview(data.branding?.logo_url || null);
      
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const saveCompany = async () => {
    if (!companyForm.name || !companyForm.name.trim()) {
      toast.error('Firmenname ist erforderlich');
      return;
    }
    
    try {
      setSaving(true);
      await settingsApi.update({ company: companyForm });
      toast.success('Unternehmensdaten gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ branding: brandingForm });
      toast.success('Bon & Branding gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Nur PNG oder JPEG erlaubt');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max 5MB)');
      return;
    }
    
    try {
      setSaving(true);
      const result = await settingsApi.uploadLogo(file);
      
      // Update preview
      setLogoPreview(result.logo_url);
      setBrandingForm({ ...brandingForm, logo_url: result.logo_url });
      
      toast.success('Logo hochgeladen');
      await loadSettings();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.message || 'Fehler beim Hochladen');
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ branding: { ...brandingForm, logo_url: '' } });
      setLogoPreview(null);
      setBrandingForm({ ...brandingForm, logo_url: '' });
      toast.success('Logo entfernt');
      await loadSettings();
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error(error.message || 'Fehler beim Entfernen');
    } finally {
      setSaving(false);
    }
  };

  const savePOS = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ pos: posForm });
      toast.success('Kasse & Verkauf gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving POS:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveTax = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ tax: taxForm });
      toast.success('Steuern gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving tax:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveInvoice = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ invoice: invoiceForm });
      toast.success('Rechnungs-Einstellungen gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const savePrinters = async () => {
    try {
      setSaving(true);
      await settingsApi.update({ printers: { items: printers } });
      toast.success('Drucker gespeichert');
      await loadSettings();
    } catch (error) {
      console.error('Error saving printers:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // OLD addPrinter function removed - using new addPrinter for cloud print system

  const handleSavePrinter = async (printer) => {
    const updatedPrinters = [...printers, printer];
    setPrinters(updatedPrinters);
    
    // Save immediately to backend
    try {
      setSaving(true);
      await settingsApi.update({ printers: { items: updatedPrinters } });
      toast.success('Drucker hinzugefügt und gespeichert');
      await loadSettings(); // Reload to get status updates
    } catch (error) {
      console.error('Error saving printer:', error);
      toast.error('Fehler beim Speichern');
      setPrinters(printers); // Rollback
    } finally {
      setSaving(false);
    }
  };

  const updatePrinterLegacy = (index, field, value) => {
    const updated = [...printers];
    updated[index][field] = value;
    setPrinters(updated);
  };

  const removePrinterLegacy = (index) => {
    setPrinters(printers.filter((_, i) => i !== index));
  };

  const testPrinter = async (printerId) => {
    try {
      const result = await settingsApi.testPrinter(printerId);
      toast.success(result.message);
    } catch (error) {
      toast.error('Testdruck fehlgeschlagen');
    }
  };

  // Save printer settings to cloud print API
  const savePrinterSettings = async (printerAssignments) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(printerAssignments)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Drucker-Zuordnung gespeichert');
      } else {
        toast.error(data.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving printer settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const tabs = [
    { id: 'legal', label: 'Legal Profile & Branding', icon: Building2 },
    { id: 'print-profiles', label: 'Druckprofile', icon: Receipt },
    { id: 'company', label: 'Unternehmen (Legacy)', icon: Building2 },
    { id: 'invoice', label: 'Rechnungen', icon: FileText },
    { id: 'printers', label: 'Drucker & Geräte', icon: Printer },
    { id: 'pos', label: 'Kasse & Verkauf', icon: ShoppingCart },
    { id: 'tax', label: 'Steuern', icon: Calculator },
    { id: 'tse', label: 'TSE', icon: ShieldCheck },
    { id: 'delivery', label: 'Delivery & Trinkgeld', icon: Truck },
  ];

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-6)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ color: 'var(--text-secondary)' }}>Lade Einstellungen...</div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" style={{ padding: 'var(--space-6)' }}>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
          Einstellungen
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Systemeinstellungen und Konfiguration verwalten
        </p>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 'var(--space-6)', borderBottom: '1px solid var(--border-primary)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', overflowX: 'auto' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                data-testid={`settings-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--accent-primary)' : '2px solid transparent',
                  color: isActive ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  whiteSpace: 'nowrap'
                }}
                className="settings-tab-button"
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', boxShadow: 'var(--shadow-md)' }}>
        
        {/* LEGAL PROFILE & BRANDING TAB (NEU) */}
        {activeTab === 'legal' && (
          <div data-testid="legal-tab">
            <LegalProfileSettings />
          </div>
        )}

        {/* PRINT PROFILES TAB (NEU) */}
        {activeTab === 'print-profiles' && (
          <div data-testid="print-profiles-tab">
            <PrintProfileSettings sessionToken={sessionStorage.getItem('authToken')} />
          </div>
        )}

        {/* UNTERNEHMEN TAB */}
        {activeTab === 'company' && (
          <div data-testid="company-tab">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Unternehmensdaten
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
              Pflichtangaben für Kassenbelege und Finanzamt
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', maxWidth: '600px' }}>
              {/* Firmenname */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Firmenname <span style={{ color: 'var(--error)' }}>*</span>
                </label>
                <input
                  data-testid="company-name-input"
                  type="text"
                  value={companyForm.name || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="z.B. Zozo Burger"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Inhaber */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Inhaber / Geschäftsführer
                </label>
                <input
                  type="text"
                  value={companyForm.owner || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, owner: e.target.value })}
                  placeholder="Max Mustermann"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Adresse */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Straße & Hausnummer
                </label>
                <input
                  type="text"
                  value={companyForm.address_line || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, address_line: e.target.value })}
                  placeholder="Musterstraße 123"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* PLZ & Stadt */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    PLZ
                  </label>
                  <input
                    type="text"
                    value={companyForm.zip || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, zip: e.target.value })}
                    placeholder="12345"
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    Stadt
                  </label>
                  <input
                    type="text"
                    value={companyForm.city || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    placeholder="Berlin"
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* Land */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Land
                </label>
                <select
                  value={companyForm.country || 'DE'}
                  onChange={(e) => setCompanyForm({ ...companyForm, country: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                </select>
              </div>

              {/* Telefon */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Telefon
                </label>
                <input
                  type="tel"
                  value={companyForm.phone || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* E-Mail */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  E-Mail
                </label>
                <input
                  type="email"
                  value={companyForm.email || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                  placeholder="info@firma.de"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* USt-ID */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Umsatzsteuer-ID
                </label>
                <input
                  type="text"
                  value={companyForm.vat_id || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, vat_id: e.target.value })}
                  placeholder="DE123456789"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Steuernummer */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Steuernummer
                </label>
                <input
                  type="text"
                  value={companyForm.tax_number || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, tax_number: e.target.value })}
                  placeholder="12/345/67890"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Divider: Bankdaten */}
              <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-primary)' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Bankverbindung (für Rechnungen)
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-4)' }}>
                  Diese Angaben erscheinen auf DIN-A4-Rechnungen
                </p>
              </div>

              {/* Bankname */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Bank
                </label>
                <input
                  type="text"
                  value={companyForm.bank_name || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, bank_name: e.target.value })}
                  placeholder="Sparkasse Berlin"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* IBAN */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  IBAN
                </label>
                <input
                  type="text"
                  value={companyForm.iban || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, iban: e.target.value })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {/* BIC */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  BIC / SWIFT
                </label>
                <input
                  type="text"
                  value={companyForm.bic || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, bic: e.target.value })}
                  placeholder="COBADEFFXXX"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {/* Kontoinhaber */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Kontoinhaber (falls abweichend)
                </label>
                <input
                  type="text"
                  value={companyForm.account_holder || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, account_holder: e.target.value })}
                  placeholder="Optional - nur bei abweichendem Inhaber"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Save Button */}
              <button
                data-testid="company-save-button"
                onClick={saveCompany}
                disabled={saving || !companyForm.name}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  background: saving || !companyForm.name ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                  color: saving || !companyForm.name ? 'var(--text-disabled)' : 'var(--text-on-accent)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: saving || !companyForm.name ? 'not-allowed' : 'pointer',
                  transition: 'all var(--transition-fast)'
                }}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {/* BON & BRANDING TAB */}
        {activeTab === 'branding' && (
          <div data-testid="branding-tab">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Bon & Branding
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
              Logo und Bontexte personalisieren
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', maxWidth: '600px' }}>
              {/* Logo Upload */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Logo
                </label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
                  Logo wird auf Bon klein gedruckt, bitte kontrastreich (PNG oder JPEG, max 5MB)
                </p>
                
                {logoPreview && (
                  <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-4)', background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)' }}>
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      style={{ maxWidth: '200px', maxHeight: '100px', display: 'block' }}
                    />
                    <button
                      onClick={removeLogo}
                      disabled={saving}
                      style={{
                        marginTop: 'var(--space-3)',
                        padding: 'var(--space-2) var(--space-4)',
                        background: 'transparent',
                        color: 'var(--error)',
                        border: '1px solid var(--error)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.75rem',
                        cursor: saving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Logo entfernen
                    </button>
                  </div>
                )}
                
                <input
                  data-testid="logo-upload-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleLogoUpload}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
              </div>

              {/* Receipt Header */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Bon-Kopfzeile
                </label>
                <input
                  data-testid="receipt-header-input"
                  type="text"
                  value={brandingForm.receipt_header || ''}
                  onChange={(e) => setBrandingForm({ ...brandingForm, receipt_header: e.target.value })}
                  placeholder="Danke für Ihren Einkauf!"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Receipt Footer */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Bon-Fußzeile
                </label>
                <textarea
                  data-testid="receipt-footer-input"
                  value={brandingForm.receipt_footer || ''}
                  onChange={(e) => setBrandingForm({ ...brandingForm, receipt_footer: e.target.value })}
                  placeholder="Auf Wiedersehen!"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Save Button */}
              <button
                data-testid="branding-save-button"
                onClick={saveBranding}
                disabled={saving}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  background: saving ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                  color: saving ? 'var(--text-disabled)' : 'var(--text-on-accent)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all var(--transition-fast)'
                }}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {/* RECHNUNGEN TAB */}
        {activeTab === 'invoice' && (
          <div data-testid="invoice-tab">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Rechnungs-Einstellungen
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
              Zahlungsbedingungen für DIN-A4-Rechnungen
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', maxWidth: '600px' }}>
              {/* Zahlungsziel (Textfeld) */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Zahlungsziel (Freitext)
                </label>
                <input
                  type="text"
                  value={invoiceForm.payment_terms || ''}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_terms: e.target.value })}
                  placeholder="Zahlbar innerhalb von 14 Tagen"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                  Dieser Text erscheint auf allen Rechnungen. Beispiele: "Sofort fällig", "Zahlbar innerhalb von 7 Tagen netto", "Betrag bereits erhalten"
                </p>
              </div>

              {/* Zahlungsziel (Tage) */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Zahlungsziel in Tagen
                </label>
                <select
                  value={invoiceForm.payment_terms_days || 14}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_terms_days: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="0">Sofort fällig (0 Tage)</option>
                  <option value="7">7 Tage</option>
                  <option value="14">14 Tage</option>
                  <option value="30">30 Tage</option>
                  <option value="60">60 Tage</option>
                  <option value="90">90 Tage</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                  Wird für Fälligkeitsberechnung genutzt (optional, derzeit nur informativ)
                </p>
              </div>

              {/* Info Box */}
              <div style={{
                padding: 'var(--space-4)',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                color: 'var(--text-primary)'
              }}>
                <strong>Hinweis:</strong> Diese Einstellungen gelten für alle neu generierten DIN-A4-Rechnungen. Bereits erstellte Rechnungen werden nicht automatisch aktualisiert.
              </div>

              {/* Save Button */}
              <button
                data-testid="invoice-save-button"
                onClick={saveInvoice}
                disabled={saving}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  background: saving ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                  color: saving ? 'var(--text-disabled)' : 'var(--text-on-accent)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all var(--transition-fast)'
                }}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}



        {/* DRUCKER TAB - Neues Drucker-Management System */}
        {activeTab === 'printers' && (
          <div data-testid="printers-tab">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-6)' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <Printer size={24} style={{ color: '#2DD4BF' }} />
                  Drucker & Geräte
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  Drucker hinzufügen und konfigurieren, welche Bons sie drucken sollen
                </p>
              </div>
              <button
                onClick={() => loadAgentToken()}
                disabled={checkingAgent}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <RefreshCw size={14} className={checkingAgent ? 'animate-spin' : ''} />
                Status aktualisieren
              </button>
            </div>

            {/* Agent Status Banner */}
            <div style={{
              padding: 'var(--space-4)',
              background: agentHealth?.agent_running 
                ? 'linear-gradient(135deg, rgba(45, 212, 191, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(239, 68, 68, 0.08) 100%)',
              border: `1px solid ${agentHealth?.agent_running ? 'rgba(45, 212, 191, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-6)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                {agentHealth?.agent_running ? (
                  <Wifi size={24} style={{ color: '#2DD4BF' }} />
                ) : (
                  <WifiOff size={24} style={{ color: '#F59E0B' }} />
                )}
                <div style={{ flex: 1 }}>
                  <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '2px' }}>
                    {agentHealth?.agent_running ? 'Cloud Print Agent verbunden' : 'Agent nicht verbunden'}
                  </strong>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {agentHealth?.agent_running 
                      ? `${agentPrinters.length} Drucker erkannt`
                      : 'Starten Sie den Agent auf Ihrem Restaurant-PC'}
                  </span>
                </div>
                {!agentHealth?.agent_running && (
                  <a
                    href="/downloads/cashx-print-agent-v4.zip"
                    download
                    style={{
                      padding: 'var(--space-2) var(--space-4)',
                      background: 'linear-gradient(135deg, #2DD4BF 0%, #22C55E 100%)',
                      color: 'white',
                      borderRadius: 'var(--radius-md)',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Download size={14} />
                    Agent herunterladen
                  </a>
                )}
              </div>
              
              {/* Wartende Druckaufträge Anzeige */}
              {queuedJobs > 0 && (
                <div style={{ 
                  marginTop: 'var(--space-3)', 
                  padding: 'var(--space-3)', 
                  background: 'rgba(59, 130, 246, 0.1)', 
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>📋</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#3B82F6', fontSize: '0.85rem' }}>
                      {queuedJobs} Druckauftrag{queuedJobs > 1 ? 'e' : ''} in Warteschlange
                    </strong>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>
                      Werden automatisch gedruckt, sobald der Agent verbunden ist
                    </p>
                  </div>
                </div>
              )}
              
              {/* Token Section - Erweitert mit Generate Button */}
              {!agentHealth?.agent_running && (
                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid rgba(251, 191, 36, 0.2)' }}>
                  {agentToken?.has_token ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <Key size={14} />
                      <span>Agent-Token:</span>
                      <code style={{ 
                        background: 'var(--bg-secondary)', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        color: '#2F80FF'
                      }}>
                        {agentToken.agent_token}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(agentToken.agent_token);
                          toast.success('Token kopiert!');
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          border: 'none',
                          color: '#2F80FF',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        data-testid="copy-agent-token-button"
                      >
                        <Copy size={12} /> Kopieren
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
                        Kein Token vorhanden. Generieren Sie einen Token für Ihre Print Agent App.
                      </p>
                      <button
                        onClick={async () => {
                          setTokenLoading(true);
                          try {
                            const response = await fetch(`${settingsApi.baseURL}/print-agent/generate-token`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`,
                                'Content-Type': 'application/json'
                              }
                            });
                            
                            if (response.ok) {
                              const data = await response.json();
                              setAgentToken({
                                has_token: true,
                                agent_token: data.agent_token
                              });
                              toast.success('Token erfolgreich generiert!');
                            } else {
                              toast.error('Token-Generierung fehlgeschlagen');
                            }
                          } catch (error) {
                            toast.error('Fehler beim Generieren des Tokens');
                          } finally {
                            setTokenLoading(false);
                          }
                        }}
                        disabled={tokenLoading}
                        style={{
                          padding: 'var(--space-2) var(--space-3)',
                          background: tokenLoading ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #2DD4BF 0%, #22C55E 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: tokenLoading ? 'not-allowed' : 'pointer',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          width: 'fit-content'
                        }}
                        data-testid="generate-agent-token-button"
                      >
                        <Key size={14} />
                        {tokenLoading ? 'Generiere...' : 'Token generieren'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* KONFIGURIERTE DRUCKER */}
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--border-primary)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Konfigurierte Drucker
                </h3>
                <button
                  onClick={() => setShowAddPrinterModal(true)}
                  disabled={!agentHealth?.agent_running || agentPrinters.length === 0}
                  data-testid="add-printer-btn"
                  style={{
                    padding: 'var(--space-2) var(--space-4)',
                    background: (!agentHealth?.agent_running || agentPrinters.length === 0) 
                      ? 'var(--bg-tertiary)' 
                      : 'linear-gradient(135deg, #2DD4BF 0%, #22C55E 100%)',
                    color: (!agentHealth?.agent_running || agentPrinters.length === 0) ? 'var(--text-secondary)' : 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: (!agentHealth?.agent_running || agentPrinters.length === 0) ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  + Drucker hinzufügen
                </button>
              </div>

              {configuredPrinters.length === 0 ? (
                <div style={{ 
                  padding: 'var(--space-6)', 
                  textAlign: 'center',
                  background: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px dashed var(--border-primary)'
                }}>
                  <Printer size={40} style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-3)', opacity: 0.5 }} />
                  <p style={{ color: 'var(--text-primary)', fontWeight: 500, margin: 0, marginBottom: '4px' }}>
                    Keine Drucker konfiguriert
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>
                    {agentHealth?.agent_running 
                      ? 'Klicken Sie auf "Drucker hinzufügen" um zu beginnen'
                      : 'Verbinden Sie zuerst den Cloud Print Agent'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                  {configuredPrinters.map((printer, idx) => (
                    <div key={printer.name} style={{
                      background: 'var(--bg-primary)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-4)',
                      border: '1px solid var(--border-primary)'
                    }}>
                      {/* Drucker Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            background: printer.enabled ? 'rgba(45, 212, 191, 0.1)' : 'var(--bg-tertiary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Printer size={20} style={{ color: printer.enabled ? '#2DD4BF' : 'var(--text-secondary)' }} />
                          </div>
                          <div>
                            <strong style={{ color: 'var(--text-primary)', display: 'block' }}>{printer.displayName || printer.name}</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              {printer.paperSize === 'a4' ? '📄 DIN A4' : printer.paperSize === '58mm' ? '🧾 58mm' : '🧾 80mm'} • {agentPrinters.find(p => p.name === printer.name)?.isDefault ? '⭐ Standard' : 'Netzwerk'}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                          {/* Papierbreite Dropdown */}
                          <select
                            value={printer.paperSize || '80mm'}
                            onChange={(e) => updatePrinterConfig(printer.name, 'paperSize', e.target.value)}
                            style={{
                              padding: '6px 8px',
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-primary)',
                              borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-primary)',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="58mm">58mm</option>
                            <option value="80mm">80mm</option>
                            <option value="a4">DIN A4</option>
                          </select>
                          {/* Testdruck Button */}
                          <button
                            onClick={() => sendTestPrint(printer)}
                            disabled={!agentHealth?.agent_running || !printer.enabled}
                            style={{
                              padding: '6px 10px',
                              background: agentHealth?.agent_running && printer.enabled ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-tertiary)',
                              border: `1px solid ${agentHealth?.agent_running && printer.enabled ? 'rgba(59, 130, 246, 0.3)' : 'var(--border-primary)'}`,
                              borderRadius: 'var(--radius-sm)',
                              color: agentHealth?.agent_running && printer.enabled ? '#3B82F6' : 'var(--text-disabled)',
                              cursor: agentHealth?.agent_running && printer.enabled ? 'pointer' : 'not-allowed',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Testdruck senden"
                          >
                            🖨️ Test
                          </button>
                          <button
                            onClick={() => updatePrinterConfig(printer.name, 'enabled', !printer.enabled)}
                            style={{
                              padding: '6px 12px',
                              background: printer.enabled ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-tertiary)',
                              border: `1px solid ${printer.enabled ? 'rgba(34, 197, 94, 0.3)' : 'var(--border-primary)'}`,
                              borderRadius: 'var(--radius-sm)',
                              color: printer.enabled ? '#22C55E' : 'var(--text-secondary)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            {printer.enabled ? '✓ Aktiv' : 'Deaktiviert'}
                          </button>
                          <button
                            onClick={() => removePrinter(printer.name)}
                            style={{
                              padding: '6px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              color: '#EF4444',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                            title="Drucker entfernen"
                          >
                            ✕
                          </button>
                        </div>
                      </div>

                      {/* Druckaufträge Checkboxen */}
                      <div style={{ 
                        borderTop: '1px solid var(--border-primary)', 
                        paddingTop: 'var(--space-3)',
                        opacity: printer.enabled ? 1 : 0.5
                      }}>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)', fontWeight: 500 }}>
                          Dieser Drucker druckt:
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                          {/* Kundenbons */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.customer ? 'rgba(45, 212, 191, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.customer ? 'rgba(45, 212, 191, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.customer || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.customer', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#2DD4BF' }}
                            />
                            <MonitorSpeaker size={16} style={{ color: '#2DD4BF' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Kundenbons</span>
                          </label>
                          
                          {/* Küchenbons */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.kitchen ? 'rgba(249, 115, 22, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.kitchen ? 'rgba(249, 115, 22, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.kitchen || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.kitchen', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#F97316' }}
                            />
                            <ChefHat size={16} style={{ color: '#F97316' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Küchenbons</span>
                          </label>
                          
                          {/* Bar-Bons */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.bar ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.bar ? 'rgba(59, 130, 246, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.bar || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.bar', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#3B82F6' }}
                            />
                            <Beer size={16} style={{ color: '#3B82F6' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Bar-Bons</span>
                          </label>
                          
                          {/* Lieferbons */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.delivery ? 'rgba(168, 85, 247, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.delivery ? 'rgba(168, 85, 247, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.delivery || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.delivery', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#A855F7' }}
                            />
                            <ShoppingCart size={16} style={{ color: '#A855F7' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Lieferbons</span>
                          </label>
                          
                          {/* Packzettel */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.packing_slip ? 'rgba(236, 72, 153, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.packing_slip ? 'rgba(236, 72, 153, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.packing_slip || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.packing_slip', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#EC4899' }}
                            />
                            <Package size={16} style={{ color: '#EC4899' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Packzettel</span>
                          </label>
                          
                          {/* Tagesabschluss */}
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: 'var(--space-2) var(--space-3)',
                            background: printer.printJobs?.daily_report ? 'rgba(107, 114, 128, 0.1)' : 'var(--bg-secondary)',
                            border: `1px solid ${printer.printJobs?.daily_report ? 'rgba(107, 114, 128, 0.3)' : 'var(--border-primary)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: printer.enabled ? 'pointer' : 'not-allowed',
                            fontSize: '0.8rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={printer.printJobs?.daily_report || false}
                              onChange={(e) => updatePrinterConfig(printer.name, 'printJobs.daily_report', e.target.checked)}
                              disabled={!printer.enabled}
                              style={{ accentColor: '#6B7280' }}
                            />
                            <FileText size={16} style={{ color: '#6B7280' }} />
                            <span style={{ color: 'var(--text-primary)' }}>Tagesabschluss</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* SPEICHERN BUTTON */}
              {configuredPrinters.length > 0 && (
                <div style={{ 
                  marginTop: 'var(--space-4)', 
                  padding: 'var(--space-4)', 
                  background: hasUnsavedChanges ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                  borderRadius: 'var(--radius-md)',
                  border: hasUnsavedChanges ? '1px solid rgba(251, 191, 36, 0.3)' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {hasUnsavedChanges && (
                    <span style={{ fontSize: '0.85rem', color: '#F59E0B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      ⚠️ Ungespeicherte Änderungen
                    </span>
                  )}
                  <button
                    onClick={handleSavePrinterConfig}
                    style={{
                      padding: 'var(--space-3) var(--space-6)',
                      background: hasUnsavedChanges 
                        ? 'linear-gradient(135deg, #2DD4BF 0%, #22C55E 100%)' 
                        : 'var(--bg-tertiary)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      color: hasUnsavedChanges ? 'white' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      marginLeft: 'auto',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    💾 Drucker-Konfiguration speichern
                  </button>
                </div>
              )}
            </div>

            {/* BON-EINSTELLUNGEN */}
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--border-primary)'
            }}>
              <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Receipt size={18} style={{ color: '#2DD4BF' }} />
                Bon-Einstellungen
              </h3>
              
              {/* Fußzeile */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                  Fußzeile auf Bons
                </label>
                <input
                  type="text"
                  value={receiptSettings.footer_message}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, footer_message: e.target.value })}
                  onBlur={() => saveReceiptSettings(receiptSettings)}
                  placeholder="Vielen Dank für Ihre Bestellung!"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              {/* Zusätzliche Zeile */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                  Zusätzlicher Text (optional)
                </label>
                <input
                  type="text"
                  value={receiptSettings.footer_subtext}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, footer_subtext: e.target.value })}
                  onBlur={() => saveReceiptSettings(receiptSettings)}
                  placeholder="z.B. Besuchen Sie uns auf Instagram @..."
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              {/* Logo URL */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                  Logo URL für Bons (optional)
                </label>
                <input
                  type="text"
                  value={receiptSettings.logo_url}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, logo_url: e.target.value })}
                  onBlur={() => saveReceiptSettings(receiptSettings)}
                  placeholder="https://example.com/logo.png"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
                <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                  Tipp: Laden Sie Ihr Logo in den Branding-Einstellungen hoch und kopieren Sie die URL hierher.
                </p>
              </div>
            </div>

            {/* Verfügbare Drucker Info */}
            {agentHealth?.agent_running && agentPrinters.length > 0 && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                border: '1px solid var(--border-primary)'
              }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
                  <strong>Erkannte Drucker:</strong> {agentPrinters.map(p => p.displayName || p.name).join(', ')}
                </p>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('access_token');
                      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cloud-print/test-print`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      toast.success('Testdruck gesendet!');
                    } catch (e) {
                      toast.error('Fehler beim Testdruck');
                    }
                  }}
                  style={{
                    padding: 'var(--space-2) var(--space-4)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Printer size={14} />
                  Testdruck senden
                </button>
              </div>
            )}

            {/* ADD PRINTER MODAL */}
            {showAddPrinterModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(4px)'
              }} onClick={() => { setShowAddPrinterModal(false); setSelectedPrinterToAdd(null); }}>
                <div style={{
                  background: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-6)',
                  width: '90%',
                  maxWidth: '500px',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  border: '1px solid var(--border-primary)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Printer size={20} style={{ color: '#2DD4BF' }} />
                    Drucker hinzufügen
                  </h3>
                  
                  {!selectedPrinterToAdd ? (
                    <>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Wählen Sie einen Drucker aus der Liste der erkannten Geräte:
                      </p>
                      
                      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                        {agentPrinters
                          .filter(p => !configuredPrinters.find(cp => cp.name === p.name))
                          .map(printer => (
                            <button
                              key={printer.name}
                              onClick={() => { setSelectedPrinterToAdd(printer); setSelectedPaperSize('80mm'); }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-3) var(--space-4)',
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                width: '100%',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'rgba(45, 212, 191, 0.1)';
                                e.currentTarget.style.borderColor = 'rgba(45, 212, 191, 0.3)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'var(--bg-secondary)';
                                e.currentTarget.style.borderColor = 'var(--border-primary)';
                              }}
                            >
                              <Printer size={20} style={{ color: printer.isDefault ? '#2DD4BF' : 'var(--text-secondary)' }} />
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: 'var(--text-primary)', display: 'block' }}>{printer.displayName || printer.name}</strong>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                  {printer.isDefault && '⭐ Standarddrucker'}
                                </span>
                              </div>
                              <span style={{ color: '#2DD4BF', fontSize: '0.8rem' }}>Auswählen →</span>
                            </button>
                          ))
                        }
                        
                        {agentPrinters.filter(p => !configuredPrinters.find(cp => cp.name === p.name)).length === 0 && (
                          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>
                            Alle erkannten Drucker wurden bereits hinzugefügt.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Schritt 2: Papierbreite auswählen */}
                      <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                        <strong style={{ color: 'var(--text-primary)' }}>{selectedPrinterToAdd.displayName || selectedPrinterToAdd.name}</strong>
                      </div>
                      
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
                        Papierbreite auswählen:
                      </p>
                      
                      <div style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                        {[
                          { value: '58mm', label: '58mm (Schmal)', desc: 'Kleine Thermodrucker' },
                          { value: '80mm', label: '80mm (Standard)', desc: 'Standard Bondrucker' },
                          { value: 'a4', label: 'DIN A4', desc: 'Normaler Drucker / Laser' }
                        ].map(size => (
                          <button
                            key={size.value}
                            onClick={() => setSelectedPaperSize(size.value)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-3)',
                              padding: 'var(--space-3) var(--space-4)',
                              background: selectedPaperSize === size.value ? 'rgba(45, 212, 191, 0.15)' : 'var(--bg-secondary)',
                              border: `2px solid ${selectedPaperSize === size.value ? '#2DD4BF' : 'var(--border-primary)'}`,
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              textAlign: 'left',
                              width: '100%',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <div style={{ 
                              width: '20px', 
                              height: '20px', 
                              borderRadius: '50%', 
                              border: `2px solid ${selectedPaperSize === size.value ? '#2DD4BF' : 'var(--border-primary)'}`,
                              background: selectedPaperSize === size.value ? '#2DD4BF' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {selectedPaperSize === size.value && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: 'var(--text-primary)', display: 'block' }}>{size.label}</strong>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{size.desc}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                      
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                          onClick={() => setSelectedPrinterToAdd(null)}
                          style={{
                            flex: 1,
                            padding: 'var(--space-3)',
                            background: 'var(--bg-tertiary)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          ← Zurück
                        </button>
                        <button
                          onClick={() => {
                            addPrinter(selectedPrinterToAdd.name, selectedPaperSize);
                            setSelectedPrinterToAdd(null);
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            flex: 1,
                            padding: 'var(--space-3)',
                            background: 'linear-gradient(135deg, #2DD4BF 0%, #22C55E 100%)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </>
                  )}
                  
                  {!selectedPrinterToAdd && (
                    <button
                      onClick={() => setShowAddPrinterModal(false)}
                      style={{
                        marginTop: 'var(--space-4)',
                        width: '100%',
                        padding: 'var(--space-3)',
                        background: 'var(--bg-tertiary)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Abbrechen
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}


        {/* TSE TAB */}
        {activeTab === 'tse' && (
          <div data-testid="tse-tab">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              TSE (Technische Sicherheitseinrichtung)
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
              TSE-Status und Informationen (Nur-Lesen)
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', maxWidth: '600px' }}>
              {/* Status Badge */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Status
                </label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-4)', background: 'rgba(34, 197, 94, 0.1)', color: 'rgb(34, 197, 94)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500 }}>
                  ✓ OK
                </div>
              </div>

              {/* Mode */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Modus
                </label>
                <input
                  type="text"
                  value={settings?.tse?.mode || 'mock'}
                  disabled
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-disabled)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Serial Number */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Seriennummer
                </label>
                <input
                  type="text"
                  value={settings?.tse?.serial_number || '-'}
                  disabled
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-disabled)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Last Signature */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Letzte Signatur
                </label>
                <input
                  type="text"
                  value={settings?.tse?.last_signature_at || 'Noch keine'}
                  disabled
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-disabled)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Info Box */}
              <div style={{ padding: 'var(--space-4)', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ fontSize: '0.875rem', color: 'rgb(59, 130, 246)', lineHeight: 1.5 }}>
                  <strong>Mock-TSE aktiv (MVP)</strong><br />
                  Für den Go-Live mit echter TSE-Hardware wird hier die echte Verbindung angezeigt.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* DELIVERY SETTINGS TAB */}
        {activeTab === 'delivery' && (
          <DeliverySettings />
        )}
      </div>

      {/* Add Printer Wizard */}
      <AddPrinterWizard
        isOpen={showAddPrinterWizard}
        onClose={() => setShowAddPrinterWizard(false)}
        onSave={handleSavePrinter}
      />
    </div>
  );
}
